/**
 * Tradução de eventos U1 para intenções da máquina de estados.
 *
 * Funções puras, sem React, para poderem ser testadas sem montar componente.
 *
 * Duas coisas diferentes saem daqui, e a separação é a mesma que o
 * `@will/contracts` faz entre `ConversationState` e `DomeState`:
 *
 * - **rota**: por onde o estado conversacional deve passar. É uma lista, não um
 *   destino, porque o contrato tem transições que exigem escala. Exemplo real:
 *   `THINKING` não chega em `TOOL_RUNNING` — só via `PREAMBLE`. Declarar a rota
 *   explicitamente preserva a validação de cada salto; um destino único
 *   obrigaria a máquina a inventar caminhos, e aí transição inválida deixaria
 *   de ser detectável.
 * O **efeito afetivo** — como o Will se apresenta — saiu daqui para
 * `expression.ts` em @will/contracts, junto com o mapa de base e a trava de
 * atividade. Motivo: existiam duas cópias do mapa estado→rosto, esta e a do
 * servidor, e elas divergiram sem que nenhum teste percebesse. O que sobra
 * neste arquivo é só a rota conversacional, que é de fato do cliente.
 */

import {
  CONVERSATION_STATES,
  TRANSITIONS,
  canTransition,
  type ConversationState,
  type U1EventType,
} from '@will/contracts';

/** Estados em que o Will está emitindo fala — controla a prop `talking` do domo. */
export function estaFalando(estado: ConversationState): boolean {
  return estado === 'ASSISTANT_SPEAKING' || estado === 'PREAMBLE';
}

const VAZIO: readonly ConversationState[] = [];

/**
 * Rota até `para`, direta se o contrato permitir, senão com uma escala.
 *
 * Existe para dois destinos de ciclo de vida — `ERROR` e `RECONNECTING` — que
 * podem chegar em qualquer momento mas não são alcançáveis de todo estado.
 * `INTERRUPTED`, por exemplo, não lista `ERROR`; sem escala, um erro durante a
 * interrupção deixaria a interface presa. A escala é sempre uma transição
 * legal, então a validação da máquina continua valendo salto a salto — o que
 * seria bug de verdade continua aparecendo no console.
 */
function viaPonte(de: ConversationState, para: ConversationState): readonly ConversationState[] {
  if (de === para) return VAZIO;
  if (canTransition(de, para)) return [para];
  const escala = TRANSITIONS[de].find((meio) => canTransition(meio, para));
  return escala ? [escala, para] : VAZIO;
}

/**
 * Rota conversacional para um evento, a partir do estado atual.
 *
 * Devolve lista vazia quando o evento não muda o que o runtime está fazendo
 * (transcrição parcial, memória persistida) ou quando o estado atual já é o
 * destino.
 */
export function rotaParaEvento(
  de: ConversationState,
  tipo: U1EventType,
): readonly ConversationState[] {
  switch (tipo) {
    case 'u1.session.ready':
      return de === 'CONNECTING' ? ['LISTENING'] : VAZIO;

    case 'u1.session.reconnecting':
      return viaPonte(de, 'RECONNECTING');

    case 'u1.session.recovered':
      return de === 'RECONNECTING' ? ['LISTENING'] : VAZIO;

    case 'u1.session.ended':
      // Não há rota legal daqui para IDLE (só `ERROR` chega em `IDLE`), e
      // encerrar em `ERROR` mentiria sobre um fim normal. Quem trata é o
      // `reset()` da máquina — ver pendência documentada em useConversation.
      return VAZIO;

    case 'u1.user.speech_started':
      // Barge-in: o usuário falando por cima interrompe o Will.
      if (de === 'ASSISTANT_SPEAKING') return ['INTERRUPTED', 'USER_SPEAKING'];
      if (de === 'INTERRUPTED') return ['USER_SPEAKING'];
      return de === 'LISTENING' ? ['USER_SPEAKING'] : VAZIO;

    case 'u1.user.speech_stopped':
      // Fim de turno do usuário: o runtime passa a processar.
      return de === 'USER_SPEAKING' ? ['THINKING'] : VAZIO;

    case 'u1.user.transcript_delta':
    case 'u1.user.transcript_final':
    case 'u1.will.transcript_delta':
    case 'u1.memory.persisted':
      return VAZIO;

    case 'u1.will.thinking':
      if (de === 'USER_SPEAKING') return ['THINKING'];
      // Turno digitado: ninguém falou, mas o contrato só chega em `THINKING`
      // passando por `USER_SPEAKING`. O texto é o turno do usuário.
      if (de === 'LISTENING') return ['USER_SPEAKING', 'THINKING'];
      return VAZIO;

    case 'u1.will.speech_started':
      if (de === 'ASSISTANT_SPEAKING') return VAZIO;
      if (de === 'THINKING' || de === 'PREAMBLE' || de === 'TOOL_RUNNING') {
        return ['ASSISTANT_SPEAKING'];
      }
      return VAZIO;

    case 'u1.will.interrupted':
      return de === 'ASSISTANT_SPEAKING' ? ['INTERRUPTED'] : VAZIO;

    case 'u1.will.speech_ended':
      if (de === 'ASSISTANT_SPEAKING' || de === 'INTERRUPTED') return ['LISTENING'];
      return VAZIO;

    case 'u1.tool.started':
    case 'u1.deep_work.started':
      // `PREAMBLE` é a fala curta de espera ("deixa eu ver isso"); o contrato
      // torna essa passagem obrigatória quando a ferramenta nasce do raciocínio.
      if (de === 'THINKING') return ['PREAMBLE', 'TOOL_RUNNING'];
      if (de === 'PREAMBLE' || de === 'WAITING_APPROVAL') return ['TOOL_RUNNING'];
      return VAZIO;

    case 'u1.tool.partial':
    case 'u1.deep_work.progress':
      return VAZIO;

    case 'u1.tool.completed':
    case 'u1.deep_work.completed':
    case 'u1.tool.failed':
    case 'u1.deep_work.cancelled':
      // O fim da ferramenta não devolve o controle sozinho: o Will ainda vai
      // falar sobre o resultado. Quem muda o estado é o `speech_started`.
      return VAZIO;

    case 'u1.approval.required':
      return de === 'TOOL_RUNNING' ? ['WAITING_APPROVAL'] : VAZIO;

    case 'u1.approval.granted':
      return de === 'WAITING_APPROVAL' ? ['TOOL_RUNNING'] : VAZIO;

    case 'u1.approval.rejected':
    case 'u1.approval.expired':
      return de === 'WAITING_APPROVAL' ? ['LISTENING'] : VAZIO;

    // Antes `u1.mission.updated` — renomeado no achado D-01, porque o nome
    // falava de missão e o payload sempre foi `{voice_mode, applies_from}`.
    // Continua sem mexer no estado conversacional: a troca de modo de voz só
    // vale do próximo turno em diante e não decide quem tem a palavra agora.
    case 'u1.session.voice_mode_changed':
      return VAZIO;

    case 'u1.privacy.enabled':
      return de === 'LISTENING' ? ['PRIVATE'] : VAZIO;

    case 'u1.privacy.disabled':
      return de === 'PRIVATE' ? ['LISTENING'] : VAZIO;

    case 'u1.state.changed':
      // Tratado à parte: o servidor é a autoridade e o destino vem no payload.
      return VAZIO;

    case 'u1.error':
      return viaPonte(de, 'ERROR');

    default:
      return VAZIO;
  }
}

export function ehEstadoConversacional(v: unknown): v is ConversationState {
  return typeof v === 'string' && (CONVERSATION_STATES as readonly string[]).includes(v);
}

/** Destino declarado por `u1.state.changed`, quando o payload traz um estado válido. */
export function estadoDeStateChanged(payload: unknown): ConversationState | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const alvo = (payload as Record<string, unknown>)['to'];
  return ehEstadoConversacional(alvo) ? alvo : null;
}
